import { Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { PdfCrossCheck, styles } from '../../../components/PDF/PDFStyle';
import { conversionLabel } from '../../../conversion';
import { roundToTwo } from '../../../MACRA_1/components/InputForm/Geometry/utils';
import { IVerticalDropsResults } from '../../../MACRA_2/api/IApiResponse';
import { IChannelGeometryVerticalDropForm } from '../../../MACRA_2/components/InputForm/ChannelGeometry/IChannelGeometry';
import { IWingsGeometryVerticalDropForm } from '../../../MACRA_2/components/InputForm/WingsGeometry/IWingsGeometry';
import { EVerticalDropsTypes } from '../../../MACRA_2/enum/general';
import { IProjectMacra2 } from '../../../MACRA_2/store/project/projectSlice';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import { getVerticalDropsType } from '../../../utils/utils';
import PDFStaticVertical from '../PDFComponents/PDFStaticVertical';
import PDFTable from '../PDFTable/PDFTable';

interface IPDFVerticalProps {
    project: IProjectMacra2;
    projectInfo: IProjectInfoForm;
    results: IVerticalDropsResults;
}

const PDFVertical = (props: IPDFVerticalProps) => {
    const { t } = useTranslation();
    const { project, projectInfo, results } = props;
    const type: EVerticalDropsTypes = getVerticalDropsType(project.stillingBasin!);
    let _string = '';
    let _label = '';
    let _hydraulicResults: string[][] = [[]];

    let _input: string[][] = [[]];

    const mainInput: string[][] = [
        [t('output_hydraulic_vertical_design_discharge'), 'Q', `${roundToTwo(project.hydraulicData?.discharge)}`, conversionLabel.m3s_ft3s[projectInfo.unitOfMeasure as number]],
        [t('output_hydraulic_vertical_roughness_coefficient'), 'n', `${roundToTwo(project.hydraulicData?.roughnessCoefficient)}`, '-'],
        [t('output_hydraulic_vertical_river_gradient'), 'i', `${roundToTwo(project.hydraulicData?.riverBedGradient)}`, conversionLabel.mm_ftft[projectInfo.unitOfMeasure as number]],
        [t('output_hydraulic_vertical_channel_width'), 'b', `${roundToTwo(project.geometry?.channelWidth)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
        [t('output_hydraulic_vertical_channel_height'), 'H', `${roundToTwo(project.wingsGeometry?.height)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
        [t('output_hydraulic_vertical_left_bank_slope'), '@0(1)', `${roundToTwo((project.geometry as IChannelGeometryVerticalDropForm).slopeOfLeftBank)}`, 'deg'],

        [t('output_hydraulic_vertical_right_bank_slope'), '@0(r)', `${roundToTwo((project.geometry as IChannelGeometryVerticalDropForm).slopeOfRightBank)}`, 'deg'],
        [t('output_hydraulic_vertical_weir_crest_elevation'), '@f(g)', `${roundToTwo(results.hydraulicResult.weirCrestElevation)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
        [
            t('output_hydraulic_vertical_weir_crest_width'),
            '@L(g)',
            `${roundToTwo((project.wingsGeometry as IWingsGeometryVerticalDropForm).width)}`,
            conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
        ],
        [t('output_hydraulic_vertical_crest_side_slope_angle'), '@0(g)', `${roundToTwo((project.wingsGeometry as IWingsGeometryVerticalDropForm).slopeAngle)}`, 'deg'],
        [
            t('output_hydraulic_vertical_granular_material_downstream'),
            '@d(90)',
            `${roundToTwo(project.soilData?.granularMaterialDownstream)}`,
            conversionLabel.mm_in[projectInfo.unitOfMeasure as number],
        ],
    ];

    switch (type) {
        case EVerticalDropsTypes.UnlinedVerticalDrop:
            _label = 'CASE 1 - UNLINED VERTICAL DROP';
            _input = mainInput;
            _hydraulicResults = [
                [t('output_hydraulic_vertical_water_depth_crest'), '@d(g)', `${roundToTwo(results.hydraulicResult.waterDepthOnCrest)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [
                    t('output_hydraulic_vertical_water_depth_downstream_structure'),
                    '@d(3)',
                    `${roundToTwo(results.hydraulicResult.waterDepthDownstreamTheStructure)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_backwater_depth'), '@d(0)', `${roundToTwo(results.hydraulicResult.backwaterDepth)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [t('output_hydraulic_vertical_discharge_coefficient'), '@c(d)', `${roundToTwo(results.hydraulicResult.dischargeCoefficient)}`, '-'],
                [t('output_hydraulic_vertical_main_weir_submerged'), '', results.hydraulicResult.isMainWeirSubmerged ? t('yes').toString() : t('no').toString(), ''],
                [
                    t('output_hydraulic_vertical_water_cushion_elevation'),
                    '@z(v)',
                    `${roundToTwo(results.hydraulicResult.waterCushionElevationAtWeirToe)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [
                    t('output_hydraulic_vertical_water_depth_supercritical_flow'),
                    '@d(1)',
                    `${roundToTwo(results.hydraulicResult.waterDepthOfSupercriticalFlow)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [
                    t('output_hydraulic_vertical_average_velocity_channel'),
                    '@V(m)',
                    `${roundToTwo(results.hydraulicResult.averageVelocityInChannel)}`,
                    conversionLabel.ms_fts[projectInfo.unitOfMeasure as number],
                ],
            ];
            break;
        case EVerticalDropsTypes.UnlinedPoolAndCountweir:
            _label = 'CASE 2 - UNLINED POOL + COUNTERWEIR';
            _input = [
                ...mainInput,
                [
                    t('output_hydraulic_vertical_counterweir_elevation'),
                    '@f(c)',
                    `${roundToTwo(project.stillingBasin?.counterweirElevation)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_counterweir_width'), '@L(c)', `${roundToTwo(project.stillingBasin?.width)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [t('output_hydraulic_vertical_basin_length_designed'), '@L(b)', `${roundToTwo(project.stillingBasin?.poolLength)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
            ];
            _hydraulicResults = [
                [t('output_hydraulic_vertical_water_depth_crest'), '@d(g)', `${roundToTwo(results.hydraulicResult.waterDepthOnCrest)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [
                    t('output_hydraulic_vertical_water_depth_downstream_structure'),
                    '@d(3)',
                    `${roundToTwo(results.hydraulicResult.waterDepthDownstreamTheStructure)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_backwater_depth'), '@d(0)', `${roundToTwo(results.hydraulicResult.backwaterDepth)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [t('output_hydraulic_vertical_main_weir_submerged'), '', results.hydraulicResult.isMainWeirSubmerged ? t('yes').toString() : t('no').toString(), ''],
                [
                    t('output_hydraulic_vertical_water_cushion_elevation'),
                    '@z(v)',
                    `${roundToTwo(results.hydraulicResult.waterCushionElevationAtWeirToe)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [
                    t('output_hydraulic_vertical_water_depth_supercritical_flow'),
                    '@d(1)',
                    `${roundToTwo(results.hydraulicResult.waterDepthOfSupercriticalFlow)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_froude_number_supercritical_flow'), '@Fr(1)', `${roundToTwo(results.hydraulicResult.froudeNumberOfSupercriticalFlow)}`, '-'],
                [t('output_hydraulic_vertical_counterweir_submerged'), '', results.hydraulicResult.isCounterweirSubmerged! ? t('yes').toString() : t('no').toString(), ''],
                [
                    t('output_hydraulic_vertical_tailwater_depth_counterweir'),
                    '@d(2)',
                    `${roundToTwo(results.hydraulicResult.tailwaterDepthDueToCounterweir)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
            ];
            break;
        case EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth:
            _label = 'CASE 3 - LINED POOL + COUNTERWEIR (fb=0)';
            _input = [
                ...mainInput,
                [
                    t('output_hydraulic_vertical_counterweir_elevation'),
                    '@f(c)',
                    `${roundToTwo(project.stillingBasin?.counterweirElevation)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_counterweir_width'), '@L(c)', `${roundToTwo(project.stillingBasin?.width)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [t('output_hydraulic_vertical_basin_length_designed'), '@L(b)', `${roundToTwo(project.stillingBasin?.poolLength)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
            ];
            _hydraulicResults = [
                [t('output_hydraulic_vertical_water_depth_crest'), '@d(g)', `${roundToTwo(results.hydraulicResult.waterDepthOnCrest)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [
                    t('output_hydraulic_vertical_water_depth_downstream_structure'),
                    '@d(3)',
                    `${roundToTwo(results.hydraulicResult.waterDepthDownstreamTheStructure)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_backwater_depth'), '@d(0)', `${roundToTwo(results.hydraulicResult.backwaterDepth)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [t('output_hydraulic_vertical_main_weir_submerged'), '', results.hydraulicResult.isMainWeirSubmerged ? t('yes').toString() : t('no').toString(), ''],
                [
                    t('output_hydraulic_vertical_water_cushion_elevation'),
                    '@z(v)',
                    `${roundToTwo(results.hydraulicResult.waterCushionElevationAtWeirToe)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [
                    t('output_hydraulic_vertical_water_depth_supercritical_flow'),
                    '@d(1)',
                    `${roundToTwo(results.hydraulicResult.waterDepthOfSupercriticalFlow)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_froude_number_supercritical_flow'), '@Fr(1)', `${roundToTwo(results.hydraulicResult.froudeNumberOfSupercriticalFlow)}`, '-'],
                [t('output_hydraulic_vertical_counterweir_submerged'), '', results.hydraulicResult.isCounterweirSubmerged! ? t('yes').toString() : t('no').toString(), ''],
                [
                    t('output_hydraulic_vertical_min_counterweir_elevation'),
                    '@f(c_min)',
                    `${roundToTwo(results.hydraulicResult.minimumCounterweirElevation)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [
                    t('output_hydraulic_vertical_verified_counterweir_elevation'),
                    '',
                    (results as IVerticalDropsResults)?.hydraulicResult?.minimumCounterweirElevation < (project as IProjectMacra2)?.stillingBasin?.counterweirElevation!
                        ? PdfCrossCheck.check
                        : PdfCrossCheck.cross,
                    '',
                ],
                [
                    t('output_hydraulic_vertical_tailwater_depth_counterweir'),
                    '@d(2)',
                    `${roundToTwo(results.hydraulicResult.tailwaterDepthDueToCounterweir)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
            ];
            break;
        case EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth:
            _label = 'CASE 4 - LINED POOL + COUNTERWEIR (fb<0)';
            _input = [
                ...mainInput,
                [t('output_hydraulic_vertical_counterweir_width'), '@L(c)', `${roundToTwo(project.stillingBasin?.width)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [
                    t('output_hydraulic_vertical_basin_elevation_designed'),
                    'fb',
                    `${roundToTwo(project.stillingBasin?.stillingBasinElevation)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_basin_length_designed'), '@L(b)', `${roundToTwo(project.stillingBasin?.poolLength)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
            ];
            _hydraulicResults = [
                [t('output_hydraulic_vertical_water_depth_crest'), '@d(g)', `${roundToTwo(results.hydraulicResult.waterDepthOnCrest)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [
                    t('output_hydraulic_vertical_water_depth_downstream_structure'),
                    '@d(3)',
                    `${roundToTwo(results.hydraulicResult.waterDepthDownstreamTheStructure)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_backwater_depth'), '@d(0)', `${roundToTwo(results.hydraulicResult.backwaterDepth)}`, conversionLabel.m_ft[projectInfo.unitOfMeasure as number]],
                [t('output_hydraulic_vertical_main_weir_submerged'), '', results.hydraulicResult.isMainWeirSubmerged ? t('yes').toString() : t('no').toString(), ''],
                [
                    t('output_hydraulic_vertical_water_cushion_elevation'),
                    '@z(v)',
                    `${roundToTwo(results.hydraulicResult.waterCushionElevationAtWeirToe)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [
                    t('output_hydraulic_vertical_water_depth_supercritical_flow'),
                    '@d(1)',
                    `${roundToTwo(results.hydraulicResult.waterDepthOfSupercriticalFlow)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [t('output_hydraulic_vertical_froude_number_supercritical_flow'), '@Fr(1)', `${roundToTwo(results.hydraulicResult.froudeNumberOfSupercriticalFlow)}`, '-'],
                [t('output_hydraulic_vertical_counterweir_submerged'), '', results.hydraulicResult.isCounterweirSubmerged! ? t('yes').toString() : t('no').toString(), ''],
                [
                    t('output_hydraulic_vertical_tailwater_depth_counterweir'),
                    '@d(2)',
                    `${roundToTwo(results.hydraulicResult.tailwaterDepthDueToCounterweir)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [
                    t('output_hydraulic_vertical_min_basin_elevation'),
                    '@f(b_min)',
                    `${roundToTwo(results.hydraulicResult.minimumBasinElevation)}`,
                    conversionLabel.m_ft[projectInfo.unitOfMeasure as number],
                ],
                [
                    t('output_hydraulic_vertical_verified_basin_elevation'),
                    '',
                    type === EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth &&
                    results?.hydraulicResult?.minimumBasinElevation! < Math.abs(project?.stillingBasin?.stillingBasinElevation!)
                        ? PdfCrossCheck.check
                        : PdfCrossCheck.cross,
                    '',
                ],
            ];
            break;

        default:
            break;
    }
    return (
        <View>
            <View style={styles.rowSpaced}>
                <Text style={styles.boldGreen}>{t('graphical_results_hydraulic')}</Text>
            </View>
            <PDFTable columns={[t('output_table_title_input'), t('output_symbol'), t('output_value'), t('output_unit')]} data={_input} />
            <PDFTable columns={[t('graphical_results_hydraulic'), t('output_symbol'), t('output_value'), t('output_unit')]} data={_hydraulicResults} />
            <PDFStaticVertical project={project} projectInfo={projectInfo} results={results} />
        </View>
    );
};

export default PDFVertical;
